import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Brain, ArrowClockwise, TrendUp, TrendDown } from "@phosphor-icons/react";
import { api } from "@/api";
import { Button } from "@/components/ui/button";

const CRITERION_LABEL = {
  htf_bull_bias: "HTF Bull Bias",
  htf_bear_bias: "HTF Bear Bias",
  ema_stack: "EMA Stack",
  rsi_bounce: "RSI Bounce",
  rsi_rejection: "RSI Rejection",
  supertrend_up: "Supertrend Up",
  supertrend_down: "Supertrend Down",
  volume_spike: "Volume Spike",
  obv_rising: "OBV Rising",
  obv_falling: "OBV Falling",
  unfilled_fvg: "Unfilled FVG",
  bull_divergence: "Bull Divergence",
  bear_divergence: "Bear Divergence",
  double_bottom: "Double Bottom",
  double_top: "Double Top",
  above_basis: "Above EMA20",
  below_basis: "Below EMA20",
  major_trend_flip: "HTF ST Flip",
};

export function LearningPanel() {
  const qc = useQueryClient();
  const [side, setSide] = useState("long");
  const [days, setDays] = useState(30);
  const [showWF, setShowWF] = useState(false);

  const learning = useQuery({
    queryKey: ["engine-learning", days],
    queryFn: () => api.engineLearning(days),
    refetchInterval: 60_000,
  });

  const walkForward = useQuery({
    queryKey: ["walk-forward", side],
    queryFn: () => api.walkForward(side, 4),
    enabled: showWF,
    refetchInterval: showWF ? 60_000 : false,
  });

  const relearn = useMutation({
    mutationFn: api.engineRelearn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["engine-learning"] });
      toast.success("Engine retrained on latest resolved signals");
    },
    onError: () => toast.error("Retrain failed"),
  });

  const reset = useMutation({
    mutationFn: api.engineReset,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["engine-learning"] });
      qc.invalidateQueries({ queryKey: ["status"] });
      toast.success("Learned weights reset — signals will resume from base scoring");
    },
    onError: () => toast.error("Reset failed"),
  });

  const data = learning.data?.[side];
  const rows = data?.criteria || [];

  return (
    <div data-testid="learning-panel" className="border border-white/10 bg-[#0A0A0A] h-full flex flex-col">
      <div className="px-4 py-3 border-b border-white/10 bg-[#0f0f0f] flex items-center gap-3 flex-wrap">
        <Brain size={16} className="text-[#F5C518]" />
        <h2 className="font-display font-bold text-sm tracking-widest uppercase text-white">Self-Learning Engine</h2>

        <div className="flex border border-white/10 ml-2" data-testid="learning-side">
          {["long", "short"].map((s) => (
            <button
              key={s}
              data-testid={`learning-side-${s}`}
              onClick={() => setSide(s)}
              className={`px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${
                side === s
                  ? s === "long"
                    ? "bg-[#00FF66] text-black"
                    : "bg-[#FF3B30] text-black"
                  : "text-white/60 hover:bg-white/5"
              }`}
            >
              {s}
            </button>
          ))}
        </div>

        <select
          data-testid="learning-days"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="bg-black border border-white/10 rounded-none px-2 py-1 text-white font-mono text-[11px]"
        >
          {[7, 14, 30, 60, 90].map((d) => (
            <option key={d} value={d}>Last {d}d</option>
          ))}
        </select>

        <span className="font-mono text-[11px] text-white/50 ml-2">
          {learning.data?.resolved_count ?? 0} resolved
        </span>

        <Button
          data-testid="engine-relearn-btn"
          onClick={() => relearn.mutate()}
          disabled={relearn.isPending}
          variant="outline"
          size="sm"
          className="ml-auto rounded-none border-white/15 bg-transparent hover:bg-white/5 text-white font-mono text-[11px] uppercase tracking-widest"
        >
          <ArrowClockwise size={12} className={relearn.isPending ? "animate-spin mr-1.5" : "mr-1.5"} />
          Retrain
        </Button>
        <Button
          data-testid="engine-reset-btn"
          onClick={() => {
            if (confirm("Reset all learned weights to base? Use this when nothing is triggering — it clears the ML skew.")) {
              reset.mutate();
            }
          }}
          disabled={reset.isPending}
          variant="outline"
          size="sm"
          className="rounded-none border-[#FF3B30]/40 bg-transparent hover:bg-[#FF3B30]/10 text-[#FF3B30] font-mono text-[11px] uppercase tracking-widest"
          title="Clear learned weights — use when nothing is triggering"
        >
          Reset
        </Button>
        <Button
          data-testid="walk-forward-btn"
          onClick={() => setShowWF((v) => !v)}
          variant="outline"
          size="sm"
          className={`rounded-none border-white/15 bg-transparent hover:bg-white/5 font-mono text-[11px] uppercase tracking-widest ${
            showWF ? "text-[#F5C518] border-[#F5C518]/50" : "text-white"
          }`}
          title="Walk-forward out-of-sample validation"
        >
          Walk-Fwd
        </Button>
      </div>

      {showWF && (
        <div className="px-4 py-3 border-b border-white/10 bg-[#0f0f0f]" data-testid="walk-forward-panel">
          <div className="flex items-center gap-3 mb-2">
            <span className="font-display text-[11px] tracking-widest uppercase text-white/70">
              Walk-Forward Validation ({side})
            </span>
            {walkForward.data?.aggregate?.oos_edge_pct_pts !== undefined && (
              <span
                className="font-mono text-[11px] font-bold"
                style={{
                  color:
                    walkForward.data.aggregate.oos_edge_pct_pts > 0
                      ? "#00FF66"
                      : walkForward.data.aggregate.oos_edge_pct_pts < 0
                      ? "#FF3B30"
                      : "#F5C518",
                }}
              >
                OOS edge: {walkForward.data.aggregate.oos_edge_pct_pts > 0 ? "+" : ""}
                {walkForward.data.aggregate.oos_edge_pct_pts} pp
              </span>
            )}
            <span className="ml-auto font-mono text-[10px] text-white/40">
              {walkForward.data?.total_samples ?? 0} samples · {walkForward.data?.n_folds ?? 0} folds
            </span>
          </div>
          {walkForward.data?.message ? (
            <div className="text-white/40 font-mono text-[11px]">{walkForward.data.message}</div>
          ) : walkForward.data ? (
            <div className="text-[11px] font-mono">
              <div className="text-white/60">
                Base:{" "}
                <span className="text-white">
                  {walkForward.data.aggregate?.base?.wins}/{walkForward.data.aggregate?.base?.count} ={" "}
                  {walkForward.data.aggregate?.base?.win_rate}%
                </span>
                {"  ·  "}
                Learned:{" "}
                <span className="text-white">
                  {walkForward.data.aggregate?.learned?.wins}/{walkForward.data.aggregate?.learned?.count} ={" "}
                  {walkForward.data.aggregate?.learned?.win_rate}%
                </span>
              </div>
              <div className="text-white/40 text-[10px] mt-1">
                Positive OOS edge → learned weights truly generalise beyond in-sample noise.
                Negative → overfitting; base weights are safer.
              </div>
            </div>
          ) : (
            <div className="text-white/40 font-mono text-[11px]">Loading…</div>
          )}
        </div>
      )}

      <div className="px-4 py-3 border-b border-white/5 text-white/60 font-mono text-[11px] leading-relaxed">
        The engine tracks the empirical <span className="text-[#00FF66]">win-rate contribution</span> of each criterion
        from every resolved signal, then automatically <span className="text-[#F5C518]">re-weights</span> future scoring.
        Criteria with positive edge get boosted (up to 1.4×), those with negative edge are down-weighted.
      </div>

      <div className="flex-1 overflow-auto">
        <table className="w-full text-left" data-testid="learning-table">
          <thead className="text-[10px] font-mono uppercase text-white/50 sticky top-0 bg-[#0f0f0f] border-b border-white/10">
            <tr>
              <th className="px-3 py-2 tracking-widest">Criterion</th>
              <th className="px-3 py-2 tracking-widest text-right">Win% Hit</th>
              <th className="px-3 py-2 tracking-widest text-right">Win% Miss</th>
              <th className="px-3 py-2 tracking-widest text-right">Edge</th>
              <th className="px-3 py-2 tracking-widest text-right">Samples</th>
              <th className="px-3 py-2 tracking-widest text-right">Base w</th>
              <th className="px-3 py-2 tracking-widest text-right">Learned w</th>
            </tr>
          </thead>
          <tbody>
            {learning.isLoading && (
              <tr>
                <td colSpan={7} className="p-6 text-center font-mono text-xs text-white/40">Loading learning stats<span className="terminal-cycle">...</span></td>
              </tr>
            )}
            {!learning.isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="p-6 text-center font-mono text-xs text-white/40">
                  Not enough resolved signals yet — the engine needs a few dozen resolutions to start learning.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const edgeColor = r.edge > 5 ? "#00FF66" : r.edge < -5 ? "#FF3B30" : "#A1A1AA";
              const delta = r.learned_weight - r.base_weight;
              const deltaColor = delta > 0 ? "#00FF66" : delta < 0 ? "#FF3B30" : "#A1A1AA";
              return (
                <tr key={r.criterion} className="border-b border-white/5" data-testid={`learn-row-${r.criterion}`}>
                  <td className="px-3 py-1.5 font-mono text-[12px] text-white">
                    {CRITERION_LABEL[r.criterion] || r.criterion}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-right text-white">{r.win_rate_when_hit}%</td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-right text-white/60">{r.win_rate_when_missed}%</td>
                  <td
                    className="px-3 py-1.5 font-mono text-[11px] text-right font-semibold flex items-center justify-end gap-1"
                    style={{ color: edgeColor }}
                  >
                    {r.edge > 0 ? <TrendUp size={11} /> : r.edge < 0 ? <TrendDown size={11} /> : null}
                    {r.edge > 0 ? "+" : ""}{r.edge}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-right text-white/60">{r.samples}</td>
                  <td className="px-3 py-1.5 font-mono text-[11px] text-right text-white/60">{r.base_weight}</td>
                  <td
                    className="px-3 py-1.5 font-mono text-[11px] text-right font-bold"
                    style={{ color: deltaColor }}
                  >
                    {r.learned_weight}
                    <span className="ml-1 text-[9px] text-white/40">
                      ({delta > 0 ? "+" : ""}{delta})
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {data?.per_symbol?.length > 0 && (
        <div className="border-t border-white/10">
          <div className="px-4 py-2 bg-[#0f0f0f] border-b border-white/10 font-display text-[11px] tracking-widest uppercase text-white/70 flex items-center gap-2">
            <Brain size={11} className="text-[#F5C518]" />
            Per-Symbol Memory
            <span className="ml-auto font-mono text-[10px] text-white/40">
              {data.per_symbol.length} pairs graduated
            </span>
          </div>
          <div className="max-h-56 overflow-auto" data-testid="learning-per-symbol">
            <table className="w-full text-left">
              <thead className="text-[10px] font-mono uppercase text-white/50 sticky top-0 bg-[#0f0f0f] border-b border-white/10">
                <tr>
                  <th className="px-3 py-1.5 tracking-widest">Symbol</th>
                  <th className="px-3 py-1.5 tracking-widest text-right">Samples</th>
                  <th className="px-3 py-1.5 tracking-widest text-right">Wins</th>
                  <th className="px-3 py-1.5 tracking-widest text-right">Win Rate</th>
                  <th className="px-3 py-1.5 tracking-widest text-right">Top Boosted</th>
                </tr>
              </thead>
              <tbody>
                {data.per_symbol.map((s) => {
                  const topCrit = Object.entries(s.weights || {}).sort((a, b) => b[1] - a[1])[0];
                  return (
                    <tr key={s.symbol} className="border-b border-white/5" data-testid={`per-symbol-${s.symbol}`}>
                      <td className="px-3 py-1.5 font-mono text-[11px] text-white">{s.symbol.replace("-USDT-SWAP", "")}</td>
                      <td className="px-3 py-1.5 font-mono text-[11px] text-right text-white/60">{s.samples}</td>
                      <td className="px-3 py-1.5 font-mono text-[11px] text-right text-white/60">{s.wins}</td>
                      <td
                        className="px-3 py-1.5 font-mono text-[11px] text-right font-semibold"
                        style={{ color: s.win_rate >= 65 ? "#00FF66" : s.win_rate >= 50 ? "#F5C518" : "#FF3B30" }}
                      >
                        {s.win_rate}%
                      </td>
                      <td className="px-3 py-1.5 font-mono text-[10px] text-right text-white/50">
                        {topCrit ? `${CRITERION_LABEL[topCrit[0]] || topCrit[0]} (${topCrit[1]})` : "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
